import { ENV } from "@/config/env";
import Logout from "@/views/user/Profile/Logout";
import { useEffect, useState } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { FiMenu } from "react-icons/fi";
import { useSelector } from "react-redux";
import {
  Link,
  NavLink,
  useLocation,
  useMatch,
  useResolvedPath,
} from "react-router-dom";

const NAV_ITEMS = [
  { label: "Home", path: "/" },
  { label: "Shop", path: "/product" },
  { label: "Guide", path: "/rudraksha" },
  { label: "About", path: "/about" },
  { label: "Blogs", path: ENV.BLOG_URL, external: true },
];


const Navigation = ({ isScrolled }) => {
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLogoutPopupOpen, setIsLogoutPopupOpen] = useState(false);
  const user = useSelector((state) => state.auth.user);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const handleOpenPopup = () => setIsLogoutPopupOpen(true);
  const handleClosePopup = () => setIsLogoutPopupOpen(false);

  useEffect(() => {
    if (!isSidebarOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isSidebarOpen]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    window.location.reload();
  };

  // Active link logic
  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/" ? "nav-link-active" : "";
    }
    return location.pathname.startsWith(path) ? "nav-link-active" : "";
  };

  // For matching paths
  const { pathname } = useResolvedPath("");
  const matchProfile = useMatch(`${pathname}/my-account/profile`);
  const matchOrders = useMatch(`${pathname}/my-account/orders`);
  const matchAddressBook = useMatch(`${pathname}/my-account/address-book`);

  const matchHome = useMatch(`${pathname}/`);
  const matchAbout = useMatch(`${pathname}/about`);
  // const matchContact = useMatch(`${pathname}/contact`);
  // const matchSignUp = useMatch(`${pathname}/register`);

  return (
    <>
      {/* Hamburger Button for small screens */}
      <button
        className={`lg:hidden text-2xl z-50 rounded-lg transition-colors duration-300 ${
          isScrolled ? "text-primary" : "text-white"
        }`}
        onClick={toggleSidebar}
      >
        <FiMenu />
      </button>

      {/* Mobile drawer */}
      <nav
        className={`fixed top-0 left-0 h-[100dvh] w-[min(85vw,18rem)] z-[200] lg:hidden transform transition-transform duration-300 ease-out overflow-y-auto bg-[#FFFAF3] text-on-surface text-sm font-body tracking-wide shadow-[0_20px_60px_rgba(0,0,0,0.22)] border-r border-black/10 ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="min-h-full bg-[#FFFAF3] px-5 py-5">
          <div className="flex items-center justify-between pb-4">
            <div className="text-xs font-black uppercase tracking-[0.28em] text-secondary/70">
              Menu
            </div>
            <button
              onClick={toggleSidebar}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-black/10 bg-white text-2xl focus:outline-none"
              aria-label="Close menu"
            >
              <AiOutlineClose />
            </button>
          </div>

          <ul className="space-y-3">
            {NAV_ITEMS.map((item) => {
              const isActive = item.external 
                ? false 
                : item.path === "/" 
                  ? matchHome 
                  : item.path === "/about" 
                    ? matchAbout 
                    : location.pathname.startsWith(item.path);
              
              return (
                <li className="px-5" key={item.label}>
                  {item.external ? (
                    <a
                      href={item.path}
                      className="block rounded-2xl bg-white px-4 py-3 text-on-surface"
                      onClick={toggleSidebar}
                    >
                      {item.label}
                    </a>
                  ) : (
                    <NavLink
                      to={item.path}
                      className={`block rounded-2xl px-4 py-3 ${
                        isActive ? "bg-primary text-white" : "bg-white text-on-surface"
                      }`}
                      onClick={toggleSidebar}
                    >
                      {item.label}
                    </NavLink>
                  )}
                </li>
              );
            })}
            {/* {!user && (
              <li>
                <NavLink
                  to="/register"
                  className={`block rounded-2xl px-4 py-3 ${
                    matchSignUp
                      ? "bg-primary text-white"
                      : "bg-white text-on-surface"
                  }`}
                  onClick={toggleSidebar}
                >
                  Sign Up
                </NavLink>
              </li>
            )} */}
          </ul>

          {user && (
            <>
              <p className="mt-6 mb-3 text-[10px] font-black uppercase tracking-[0.3em] text-secondary/60">
                Manage My Account
              </p>
              <ul className="space-y-3">
                <li>
                  <NavLink
                    to="/my-account/profile"
                    className={`block rounded-2xl px-4 py-3 ${
                      matchProfile
                        ? "bg-primary text-white"
                        : "bg-white text-on-surface"
                    }`}
                    onClick={toggleSidebar}
                  >
                    My Profile
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/my-account/address-book"
                    className={`block rounded-2xl px-4 py-3 ${
                      matchAddressBook
                        ? "bg-primary text-white"
                        : "bg-white text-on-surface"
                    }`}
                    onClick={toggleSidebar}
                  >
                    Address Book
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/my-account/orders"
                    className={`block rounded-2xl px-4 py-3 ${
                      matchOrders
                        ? "bg-primary text-white"
                        : "bg-white text-on-surface"
                    }`}
                    onClick={toggleSidebar}
                  >
                    My Orders
                  </NavLink>
                </li>
                <li>
                  <button
                    onClick={() => {
                      toggleSidebar();
                      handleOpenPopup();
                    }}
                    className="w-full rounded-2xl bg-white px-4 py-3 text-left text-on-surface"
                  >
                    Logout
                  </button>
                </li>
              </ul>
            </>
          )}
        </div>
      </nav>

      <div
        className={`hidden lg:flex items-center mr-5 xl:mr-auto transition-all duration-300 font-body text-[13px] font-bold tracking-[0.2em] uppercase relative overflow-hidden ${
          isScrolled
            ? "text-on-surface hover:text-primary transition-all duration-300"
            : "text-white hover:text-primary transition-all duration-300"
        }`}
        style={{
          background: isScrolled ? 'transparent' : 'linear-gradient(135deg, rgba(181, 38, 25, 0.15) 0%, rgba(181, 38, 25, 0.08) 100%)',
          padding: isScrolled ? '0' : '0.75rem 1.5rem',
          borderRadius: isScrolled ? '0' : '16px',
          border: isScrolled ? 'none' : '1px solid rgba(181, 38, 25, 0.4)',
          boxShadow: isScrolled ? 'none' : '0 0 30px rgba(181, 38, 25, 0.3), inset 0 0 30px rgba(181, 38, 25, 0.15), 0 0 60px rgba(181, 38, 25, 0.1)',
          backdropFilter: isScrolled ? 'none' : 'blur(15px)',
          position: 'relative',
        }}
      >
        {/* Hologram Effect */}
        {!isScrolled && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%)',
              backgroundSize: '200% 200%',
              animation: 'hologram 3s ease-in-out infinite',
              pointerEvents: 'none',
              borderRadius: '16px',
              zIndex: 0,
            }}
          />
        )}
        {/* Logo Behind */}
        {!isScrolled && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              opacity: 0.1,
              width: '80%',
              height: '80%',
              backgroundImage: 'url(/Logo.png)',
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              pointerEvents: 'none',
              zIndex: 0,
            }}
          />
        )}
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: '3rem' }}>
          {NAV_ITEMS.map((item) => {
            if (item.external) {
              return (
                <a
                  key={item.label}
                  href={item.path}
                  className="nav-link"
                  style={{
                    "--nav-underline-color": isScrolled ? "var(--color-primary)" : "#fff",
                  }}
                >
                  {item.label}
                </a>
              );
            }
            return (
              <Link
                key={item.label}
                to={item.path}
                className={`nav-link ${isActive(item.path)}`}
                style={{
                  "--nav-underline-color": isScrolled ? "var(--color-primary)" : "#fff",
                }}
              >
                {item.label}
              </Link>
            );
          })}
        </div>
      </div>

      <Logout
        isOpen={isLogoutPopupOpen}
        onClose={handleClosePopup}
        onConfirm={handleLogout}
      />
    </>
  );
};

export default Navigation;
